import { useState } from 'react';
import type { SortingState } from '@tanstack/react-table';
import type {
  DocumentTemplateListParams,
  DocumentType,
  TemplateOwner,
  TemplateStatusFilter,
} from '../types/documentTemplates.types';
import { templateListSort } from '../utils/templateListSort';

export interface TemplateListFilters {
  documentType?: DocumentType;
  status?: TemplateStatusFilter;
  owner?: TemplateOwner;
  dealerId?: string;
}

const DEFAULT_SORTING: SortingState = [{ id: 'updated_at', desc: true }];

export function useTemplateListFilters() {
  const [search, setSearchState] = useState('');
  const [filters, setFilters] = useState<TemplateListFilters>({});
  const [sorting, setSortingState] = useState<SortingState>(DEFAULT_SORTING);
  const [page, setPage] = useState(1);
  const [perPage, setPerPageState] = useState(20);

  const update = (next: (current: TemplateListFilters) => TemplateListFilters) => {
    setFilters(next);
    setPage(1);
  };

  return {
    search,
    filters,
    sorting,
    activeCount: Object.values(filters).filter(Boolean).length,
    params: {
      ...filters,
      ...templateListSort(sorting),
      search: search || undefined,
      page,
      perPage,
    } satisfies DocumentTemplateListParams,
    setSearch: (term: string) => {
      setSearchState(term);
      setPage(1);
    },
    setFilter: <K extends keyof TemplateListFilters>(key: K, value: TemplateListFilters[K]) =>
      update((current) => ({ ...current, [key]: value })),
    setOwner: (owner: TemplateOwner | undefined) =>
      update((current) => ({ ...current, owner, dealerId: owner === 'platform' ? undefined : current.dealerId })),
    clearFilters: () => update(() => ({})),
    setSorting: (next: SortingState) => {
      setSortingState((current) => (next.length > 0 ? next : [{ id: current[0].id, desc: !current[0].desc }]));
      setPage(1);
    },
    setPage,
    setPerPage: (next: number) => {
      setPerPageState(next);
      setPage(1);
    },
  };
}
